// ============================================================================
// IN-PROGRESS CLEANING TASKS — All incomplete in_progress tasks for this user.
// Cleaners may have several open jobs; each appears under "In progress" on the schedule.
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { getTaskIdsWithChecklistAssignmentRows } from "@/lib/cleaningTaskChecklistAssignments";
import type { ActiveCleaningTask } from "@/types/cleaning";

export type { ActiveCleaningTask };

type TaskRow = {
  id: string;
  property_id: string;
  status: string;
  scheduled_date: string;
  started_at: string | null;
  checklist_completed: boolean | null;
  inventory_completed: boolean | null;
  assigned_type: string | null;
};

const isIncomplete = (t: TaskRow) => !(t.checklist_completed && t.inventory_completed);

export const useInProgressCleaningTasks = () => {
  const { user } = useAuth();
  const { data: cleaner } = useCleanerRecord();

  return useQuery({
    queryKey: ["in-progress-cleaning-tasks", user?.id, cleaner?.id],
    queryFn: async (): Promise<ActiveCleaningTask[]> => {
      if (!user?.id) return [];

      const byId = new Map<string, TaskRow>();

      if (cleaner?.id) {
        const { data: viaAssign } = await supabase
          .from("cleaning_task_checklist_assignments")
          .select("cleaning_task_id")
          .eq("cleaner_id", cleaner.id);
        const viaIds = new Set((viaAssign ?? []).map((r) => r.cleaning_task_id));

        const { data: cleanerTasks, error: e1 } = await supabase
          .from("cleaning_tasks")
          .select(
            "id, property_id, status, scheduled_date, started_at, checklist_completed, inventory_completed, assigned_type"
          )
          .eq("cleaner_id", cleaner.id)
          .in("assigned_type", ["cleaner", "unassigned"])
          .eq("status", "in_progress")
          .order("scheduled_date", { ascending: true })
          .order("created_at", { ascending: true });
        if (!e1 && cleanerTasks && cleanerTasks.length > 0) {
          const withJunction = await getTaskIdsWithChecklistAssignmentRows(cleanerTasks.map((t) => t.id));
          for (const t of cleanerTasks as TaskRow[]) {
            if (withJunction.has(t.id) && !viaIds.has(t.id)) continue;
            if (isIncomplete(t)) byId.set(t.id, t);
          }
        }

        const viaIdList = [...viaIds];
        if (viaIdList.length > 0) {
          const { data: viaInProgress, error: e1b } = await supabase
            .from("cleaning_tasks")
            .select(
              "id, property_id, status, scheduled_date, started_at, checklist_completed, inventory_completed, assigned_type"
            )
            .in("id", viaIdList)
            .eq("status", "in_progress")
            .order("scheduled_date", { ascending: true })
            .order("created_at", { ascending: true });
          if (!e1b && viaInProgress) {
            for (const t of viaInProgress as TaskRow[]) {
              if (isIncomplete(t)) byId.set(t.id, t);
            }
          }
        }
      }

      const { data: ownerTeamTasks, error: e2 } = await supabase
        .from("cleaning_tasks")
        .select(
          "id, property_id, status, scheduled_date, started_at, checklist_completed, inventory_completed, assigned_type"
        )
        .eq("assignee_user_id", user.id)
        .in("assigned_type", ["owner", "team"])
        .eq("status", "in_progress")
        .order("scheduled_date", { ascending: true })
        .order("created_at", { ascending: true });
      if (!e2 && ownerTeamTasks) {
        for (const t of ownerTeamTasks as TaskRow[]) {
          if (isIncomplete(t)) byId.set(t.id, t);
        }
      }

      const tasks = [...byId.values()].sort(
        (a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || a.id.localeCompare(b.id)
      );
      if (tasks.length === 0) return [];

      const propertyIds = [...new Set(tasks.map((t) => t.property_id))];
      const { data: properties } = await supabase.from("properties").select("id, name, address").in("id", propertyIds);
      const propMap = new Map(
        (properties ?? []).map((p: { id: string; name?: string | null; address?: string | null }) => [
          p.id,
          { name: p.name ?? null, address: p.address ?? null },
        ])
      );

      return tasks.map((task) => {
        const p = propMap.get(task.property_id);
        const assignedType = (task.assigned_type ?? "cleaner") as "owner" | "team" | "cleaner";
        return {
          id: task.id,
          property_id: task.property_id,
          status: task.status,
          scheduled_date: task.scheduled_date,
          started_at: task.started_at ?? null,
          checklist_completed: task.checklist_completed,
          inventory_completed: task.inventory_completed,
          property_name: p?.name ?? "",
          assigned_type: assignedType,
          property_address: p?.address ?? null,
        } satisfies ActiveCleaningTask;
      });
    },
    enabled: !!user?.id,
  });
};

/** First incomplete in-progress task (legacy callers that expect a single “active” row). */
export const useActiveCleaningTask = () => {
  const q = useInProgressCleaningTasks();
  return {
    ...q,
    data: q.data?.[0] ?? null,
  };
};
